import * as React from 'react';
import { Breadcrumb, Button, Col, Collapse, Row, BreadcrumbItem } from 'reactstrap';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import BarChartView from './ChartViews/BarChartView';
import PieChartView from './ChartViews/PieChartView';
import { clearBarChartData, clearPieChartData, DataPoint } from '../../../redux/VisualizationSlice';
import { getReportYears, selectAvailableReportYears } from '../../../redux/ReportSlice';
import { TaxonomyTypeEnum } from '../../../redux/TaxonomyVersionSlice';

export interface ViewOption {
    value: string;
    label: string;
    iframeClassName: string;
}

const VisualizationView = (props: any) => {


    const dispatch = useAppDispatch();

    const availableReportYears = useAppSelector(selectAvailableReportYears);

    const viewOptions: ViewOption[] = [

        { label: "Explore Revenues", value: "REVENUES", iframeClassName: "bi-clipboard-plus" },
        { label: "Explore Expenditures", value: "EXPENDITURES", iframeClassName: "bi-clipboard-minus" },
        //{ label: "Explore Balance Sheets", value: "BALANCE_SHEET", iframeClassName: "bi-clipboard-data" },
        //{ label: "Explore Population", value: "POPULATION", iframeClassName: "bi bi-people" },
        //{ label: "Explore Long-Term Debt", value: "LONG-TERM_DEBT", iframeClassName: "bi bi-piggy-bank" }

    ];

    const entityTypeValues = [
        { label: "All", value: "ALL" },
        { label: "Counties", value: "COUNTIES" },
        { label: "Cities", value: "CITIES" },
        { label: "Special Districts", value: "SPECIAL_DISTRICTS" },
        { label: "Other Entities", value: "OTHER_ENTITIES" }
    ];

    const defaultValue = { label: "please select", value: "default" };

    const [pageMode, setPageMode] = React.useState<'LANDING' | 'BAR_CHART' | 'PIE_CHART'>('LANDING');

    const [breadCrumbData, setBreadCrumbData] = React.useState<Array<string>>(["Visualization"]);

    const [selectedViewOption, setSelectedViewOption] = React.useState<ViewOption | null>(null);
    const [selectedAccountType, setSelectedAccountType] = React.useState<{ label: string; value: string }>({ label: "", value: "" });
    const [selectedDataPoint, setSelectedDataPoint] = React.useState<DataPoint | null>(null);
    const [selectedFiscalYear, setSelectedFiscalYear] = React.useState<{ label: string; value: string }>(defaultValue);
    const [rerenderPieChart, setRerenderPieChart] = React.useState<boolean>(false);


    const [selectedEntityType, setSelectedEntityType] = React.useState<{ label: string; value: string }>(entityTypeValues[0]);
    const [entitySearchTerm, setEntitySearchTerm] = React.useState<string>('');

    const isDollarAmount = (): boolean => {
        let result: boolean = false;
        if (selectedViewOption) {

            result = selectedViewOption.value == "REVENUES"
                || selectedViewOption.value == "EXPENDITURES"
                || selectedViewOption.value == "LONG-TERM_DEBT"
                || selectedViewOption.value == "BALANCE_SHEET";
        }
        return result;
    }

    const formatNumericValue = (value: number): string => {
        const absValue = Math.abs(value);
        const isNegative = value < 0;
        const dollarSign = isDollarAmount() ? "$" : "";

        let formattedValue: string;

        if (absValue >= 1000000000) {
            formattedValue = dollarSign + (absValue / 1000000000).toFixed(2) + ' Billion';
        } else if (absValue >= 1000000) {
            formattedValue = dollarSign + (absValue / 1000000).toFixed(2) + ' Million';
        } else if (absValue >= 1000) {
            formattedValue = dollarSign + (absValue / 1000).toFixed(2) + ' Thousand';
        } else if (absValue >= 100) {
            formattedValue = dollarSign + (absValue / 100).toFixed(2) + ' Hundred';
        } else {
            formattedValue = dollarSign + absValue.toFixed(2);
        }

        // Use accounting notation with negative sign inside parentheses
        return isNegative ? `-${formattedValue}` : formattedValue;
    };

    const getDisplayFromBreadCrumbExcludeAccountCode = (crumb: string, crumbIndex: number): string => {
        let result: string = crumb;

        if (crumbIndex > 2) {
            const separator = " - ";
            const lastIndex = crumb.lastIndexOf(separator);
            if (lastIndex !== -1) {
                result = crumb.substring(lastIndex + separator.length);
            }
        }
        return result;
    }

    React.useEffect(() => {

        if (selectedViewOption) {
            setSelectedAccountType({ label: selectedViewOption.label, value: selectedViewOption.value });

            if (selectedViewOption.value !== "") {
                var newBreadCrumbData: Array<string> = ["Visualization", selectedViewOption.label.replace("Explore ", "")];
                setBreadCrumbData(newBreadCrumbData);
            } else {
                setBreadCrumbData(["Visualization"]);
            }


        } else {
            setSelectedAccountType({ label: "", value: "" });
        }

    }, [selectedViewOption]);

    React.useEffect(() => {
        if (!availableReportYears || availableReportYears.length === 0) {
            dispatch(getReportYears(TaxonomyTypeEnum.AFR));
        }
        if (breadCrumbData.length === 0) {
            setBreadCrumbData(["Visualization"]);
        }
    }, []);

    React.useEffect(() => {

        return () => { clearBarChartData(); clearPieChartData(); }

    }, []);

    React.useEffect(() => {

        if (selectedDataPoint && selectedDataPoint.label && selectedDataPoint.label !== "") {

            if (breadCrumbData) {
                let updatedBreadCrumbs: Array<string> = breadCrumbData.slice(0, 2);
                updatedBreadCrumbs.push(selectedDataPoint.x);
                setBreadCrumbData(updatedBreadCrumbs);
            }
        }

    }, [selectedDataPoint])

    const landingPageRender = (<React.Fragment>
        <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '70vh',
            padding: '20px'
        }}>
            <Row>
                {
                    viewOptions.map((option: ViewOption, idx: number) => (
                        <Col key={option.value.toString() + "_" + idx.toString()}>
                            <Button className="taskLinkButton"
                                style={{
                                    borderRadius: '1em',
                                    textAlign: 'center',
                                    height: '6em',
                                    width: '6em',
                                    fontSize: '3.5rem'
                                }}
                                onClick={() => {
                                    setSelectedViewOption(option);
                                    setPageMode('BAR_CHART');
                                    setBreadCrumbData(["Visualization", option.label.replace("Explore ", "")]);
                                }}
                            ><i className={option.iframeClassName}></i>
                                <Row>
                                    <Col>
                                        <h4>{option.label}</h4>
                                    </Col>
                                </Row>
                            </Button>
                        </Col>
                    ))
                }
            </Row>
        </div>
    </React.Fragment>);

    const barChartRender = (<React.Fragment>
        <BarChartView
            pageMode={pageMode}
            setPageMode={setPageMode}
            isDollarAmount={isDollarAmount}
            formatNumericValue={formatNumericValue}
            selectedAccountType={selectedAccountType}
            selectedDataPoint={selectedDataPoint}
            setSelectedDataPoint={setSelectedDataPoint}
            selectedFiscalYear={selectedFiscalYear}
            setSelectedFiscalYear={setSelectedFiscalYear}
            selectedEntityType={selectedEntityType}
            setSelectedEntityType={setSelectedEntityType}
            entitySearchTerm={entitySearchTerm}
            setEntitySearchTerm={setEntitySearchTerm}
            breadCrumbData={breadCrumbData}
            setBreadCrumbData={setBreadCrumbData}
        />
    </React.Fragment>);

    const pieChartRender = (<React.Fragment>
        <PieChartView
            pageMode={pageMode}
            setPageMode={setPageMode}
            isDollarAmount={isDollarAmount}
            formatNumericValue={formatNumericValue}
            selectedAccountType={selectedAccountType}
            selectedDataPoint={selectedDataPoint}
            setSelectedDataPoint={setSelectedDataPoint}
            selectedFiscalYear={selectedFiscalYear}
            breadCrumbData={breadCrumbData}
            setBreadCrumbData={setBreadCrumbData}
            rerenderPieChart={rerenderPieChart}
            setRerenderPieChart={setRerenderPieChart}
        />
    </React.Fragment>);

    const BreadcrumbNavigationRender = (<Breadcrumb className="visualization-chart-breadcrumb ">

        {breadCrumbData.map((crumb, index) => {
            const isActive = index !== breadCrumbData.length - 1;
            return (
                <BreadcrumbItem
                    key={index}
                    active={isActive}
                    style={{
                        cursor: isActive ? 'pointer' : 'default',
                        marginTop: '0em',
                    }}
                    onClick={() => {
                        if (!isActive) { return }
                        else {

                            switch (crumb) {
                                case 'Visualization':
                                    dispatch(clearBarChartData());
                                    dispatch(clearPieChartData());
                                    setPageMode('LANDING');
                                    setBreadCrumbData(["Visualization"]);
                                    break;
                                case 'Revenues':
                                case 'Expenditures':
                                case 'Balance Sheets':
                                case 'Population':
                                case 'Long-Term Debt':
                                    dispatch(clearPieChartData());
                                    if (selectedViewOption) {
                                        var newBreadCrumbData: Array<string> = ["Visualization", selectedViewOption.label.replace("Explore ", "")];
                                        setBreadCrumbData(newBreadCrumbData);
                                    }
                                    setPageMode('BAR_CHART');
                                    break;
                                default:
                                    //for pie clicks the bread crumb must be updated to the clicked crumb and all crumbs after it removed
                                    let indexOfCrumb: number = breadCrumbData.indexOf(crumb);
                                    let subsetBreadCrumbData: Array<string> = breadCrumbData.slice(0, indexOfCrumb + 1);
                                    setBreadCrumbData(subsetBreadCrumbData);
                                    setRerenderPieChart(true);
                                    break;
                            }
                        }
                    }}
                >
                    {getDisplayFromBreadCrumbExcludeAccountCode(crumb, index)}
                </BreadcrumbItem>
            )
        })} </Breadcrumb>)


    return (<React.Fragment>
        <Row>
            <Col>
                {BreadcrumbNavigationRender}
            </Col>
        </Row>
        <Row>
            <Col>
                {pageMode === 'LANDING' && landingPageRender}
                {pageMode === 'BAR_CHART' && barChartRender}
                {pageMode === 'PIE_CHART' && pieChartRender}
            </Col>
        </Row>
    </React.Fragment>);
}

export default VisualizationView;;